import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  MessageSquare, Settings, Send, Check, X, Clock, 
  AlertCircle, FileText, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import AppHeader from '@/components/shared/AppHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SMSSettings = ({ user, onLogout }) => {
  const [status, setStatus] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('settings');
  
  // Settings form
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [reminderHours, setReminderHours] = useState(1);
  
  // Test SMS form
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [sending, setSending] = useState(false);
  
  // Template editing
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateText, setTemplateText] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statusRes, templatesRes, logsRes] = await Promise.all([
        axios.get(`${API}/sms/status`, { headers }),
        axios.get(`${API}/sms/templates`, { headers }),
        axios.get(`${API}/sms/logs?limit=50`, { headers })
      ]);
      
      setStatus(statusRes.data);
      setSmsEnabled(statusRes.data.sms_enabled);
      setReminderHours(statusRes.data.reminder_hours_before);
      setTemplates(templatesRes.data);
      setLogs(logsRes.data);
    } catch (error) {
      toast.error('Failed to load SMS settings');
    }
    setLoading(false);
  };

  const saveSettings = async () => {
    try {
      await axios.post(`${API}/sms/settings`, {
        sms_enabled: smsEnabled,
        reminder_hours_before: reminderHours
      }, { headers });
      toast.success('SMS settings saved');
      loadData();
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const sendTestSMS = async () => {
    if (!testPhone || !testMessage) {
      toast.error('Please enter phone and message');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/sms/send`, {
        to_phone: testPhone,
        message: testMessage,
        template_type: 'test'
      }, { headers });
      
      if (response.data.status === 'logged') {
        toast.info('SMS logged (service disabled)');
      } else {
        toast.success('SMS sent successfully');
      }
      setTestPhone('');
      setTestMessage('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    }
    setSending(false);
  };

  const saveTemplate = async () => {
    if (!editingTemplate || !templateText) return;

    try {
      await axios.post(`${API}/sms/templates`, {
        template_type: editingTemplate,
        template_text: templateText,
        is_active: true
      }, { headers });
      
      toast.success('Template saved');
      setEditingTemplate(null);
      setTemplateText('');
      loadData();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const getStatusBadge = (logStatus) => {
    switch (logStatus) {
      case 'sent': return <Badge className="bg-green-100 text-green-800">Sent</Badge>;
      case 'failed': return <Badge className="bg-red-100 text-red-800">Failed</Badge>;
      case 'disabled': return <Badge className="bg-gray-100 text-gray-800">Logged</Badge>;
      default: return <Badge>{logStatus}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader title="SMS Settings" showBack onLogout={onLogout} />
        <div className="max-w-4xl mx-auto px-4 py-12 text-center text-muted-foreground">
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader title="SMS Settings" showBack onLogout={onLogout} />
      
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="settings" data-testid="sms-settings-tab">
              <Settings className="w-4 h-4 mr-2" /> Settings
            </TabsTrigger>
            <TabsTrigger value="templates" data-testid="sms-templates-tab">
              <FileText className="w-4 h-4 mr-2" /> Templates
            </TabsTrigger>
            <TabsTrigger value="logs" data-testid="sms-logs-tab">
              <Clock className="w-4 h-4 mr-2" /> Logs
            </TabsTrigger>
            <TabsTrigger value="test" data-testid="sms-test-tab">
              <Send className="w-4 h-4 mr-2" /> Test
            </TabsTrigger>
          </TabsList>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="glass rounded-2xl p-6 space-y-6">
              <h2 className="text-lg font-semibold">SMS Configuration</h2>
              
              {/* Status */}
              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  {status?.twilio_configured ? (
                    <Check className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                  )}
                  <div>
                    <p className="font-medium">
                      Twilio: {status?.twilio_configured ? 'Configured' : 'Not Configured'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Sender: {status?.default_sender}
                    </p>
                  </div>
                </div>
              </div>

              {/* Enable SMS */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Enable SMS Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    When disabled, SMS will be logged but not sent
                  </p>
                </div>
                <Switch 
                  checked={smsEnabled} 
                  onCheckedChange={setSmsEnabled}
                  data-testid="sms-enable-toggle"
                />
              </div>

              {/* Reminder Hours */}
              <div>
                <Label>Appointment Reminder (hours before)</Label>
                <Select 
                  value={String(reminderHours)} 
                  onValueChange={(v) => setReminderHours(parseInt(v))}
                >
                  <SelectTrigger className="w-40 mt-2" data-testid="reminder-hours-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 hour</SelectItem>
                    <SelectItem value="2">2 hours</SelectItem>
                    <SelectItem value="3">3 hours</SelectItem>
                    <SelectItem value="24">24 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={saveSettings} data-testid="save-sms-settings">
                Save Settings
              </Button>

              {/* Setup Instructions */}
              {!status?.twilio_configured && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h3 className="font-medium text-yellow-800 mb-2">Setup Required</h3>
                  <p className="text-sm text-yellow-700 mb-2">
                    To enable SMS notifications, add these environment variables:
                  </p>
                  <code className="block text-xs bg-yellow-100 p-2 rounded">
                    TWILIO_ACCOUNT_SID=your_account_sid<br/>
                    TWILIO_AUTH_TOKEN=your_auth_token<br/>
                    TWILIO_PHONE_NUMBER=+1234567890
                  </code>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates">
            <div className="glass rounded-2xl p-6 space-y-4">
              <h2 className="text-lg font-semibold">SMS Templates</h2>
              <p className="text-sm text-muted-foreground">
                Customize message templates. Use {'{variable}'} for dynamic content.
              </p>
              
              <div className="space-y-4">
                {templates.map((template) => (
                  <div key={template.template_type} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-medium capitalize">
                          {template.template_type.replace(/_/g, ' ')}
                        </h3>
                        {template.is_default && (
                          <Badge variant="outline" className="text-xs">Default</Badge>
                        )}
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setEditingTemplate(template.template_type);
                          setTemplateText(template.template_text);
                        }}
                      >
                        Edit
                      </Button>
                    </div>
                    <pre className="text-sm text-muted-foreground whitespace-pre-wrap bg-muted/50 p-2 rounded">
                      {template.template_text}
                    </pre>
                  </div>
                ))}
              </div>

              {/* Template Editor */}
              {editingTemplate && (
                <div className="border-t pt-4 mt-4">
                  <h3 className="font-medium mb-2">
                    Editing: {editingTemplate.replace(/_/g, ' ')}
                  </h3>
                  <Textarea
                    value={templateText}
                    onChange={(e) => setTemplateText(e.target.value)}
                    rows={6}
                    className="font-mono text-sm"
                  />
                  <div className="flex gap-2 mt-3">
                    <Button onClick={saveTemplate}>Save Template</Button>
                    <Button variant="outline" onClick={() => setEditingTemplate(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Logs Tab */}
          <TabsContent value="logs">
            <div className="glass rounded-2xl p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">SMS Logs</h2>
                <Button size="sm" variant="outline" onClick={loadData}>
                  <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                </Button>
              </div>
              
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {logs.length > 0 ? logs.map((log) => (
                  <div key={log._id} className="flex justify-between items-start p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">{log.to_phone}</p>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {log.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(log.timestamp).toLocaleString()} • {log.template_type || 'manual'}
                      </p>
                      {log.error && (
                        <p className="text-xs text-red-600 mt-1">{log.error}</p>
                      )}
                    </div>
                    {getStatusBadge(log.status)}
                  </div>
                )) : (
                  <p className="text-center text-muted-foreground py-8">No SMS logs yet</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Test Tab */}
          <TabsContent value="test">
            <div className="glass rounded-2xl p-6 space-y-4">
              <h2 className="text-lg font-semibold">Send Test SMS</h2>
              <p className="text-sm text-muted-foreground">
                Send a test message to verify SMS configuration
              </p>
              
              <div>
                <Label>Phone Number</Label>
                <Input
                  placeholder="+91XXXXXXXXXX"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  className="mt-1"
                  data-testid="test-sms-phone"
                />
              </div>
              
              <div>
                <Label>Message</Label>
                <Textarea
                  placeholder="Enter test message..."
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  className="mt-1"
                  rows={4}
                  data-testid="test-sms-message"
                />
              </div>
              
              <Button 
                onClick={sendTestSMS} 
                disabled={sending || !testPhone || !testMessage}
                data-testid="send-test-sms"
              >
                {sending ? 'Sending...' : 'Send Test SMS'}
              </Button>
              
              {!status?.twilio_configured && (
                <p className="text-sm text-yellow-600">
                  Note: Twilio not configured. SMS will be logged but not actually sent.
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SMSSettings;
